import { Form, Formik } from 'formik';
import React from 'react';
import * as Yup from 'yup';
import { MediumButton, PrimaryButton } from '../../../../components/Buttons';
import { FormField } from '../../../../components/FormElements';
import HelpAndInfoBox from '../../../../components/HelpAndInfoBox';
import { ModalBody, ModalContainer, ModalFooter, ModalHeader } from '../../../../components/ModalElements';
import { useUser } from '../../../authentication';
import alertService from '../../../../services/AlertService';
import windowService from '../../../../services/windowService';
import { addCoursesToPathway } from '../../api/addCoursesToPathway';
import { cloneCourse } from '../../api/cloneCourse';
import { Pathway, PathwayEntity } from '../../models/pathway';
import useManageTrainingRoute from '../../../../hooks/useManageTrainingRoute';
import { useParams } from 'react-router-dom';
import { handleError } from '../../../../utils/apiUtils';
import { removeCourse } from '../../api/removeCourse';
import { removeStepFromPathway } from '../../api/removeStepFromPathway';

type Props = {
  handleDismiss: () => void;
  pathway: Pathway;
  originalCourse: PathwayEntity;
  position: number;
  removeEntity: (entityId: string) => Promise<void>;
};

export const ReplaceUnownedCourseModal = ({
  handleDismiss,
  pathway,
  originalCourse,
  position,
  removeEntity,
}: Props) => {
  const user = useUser();
  const { manageTrainingRoutePrefix } = useManageTrainingRoute();
  const { trainingId } = useParams<{ trainingId: string }>();
  const originalCourseId = originalCourse?.taskListId?.id;

  if (!originalCourseId || !user?.userId) {
    alertService.showError();
    console.error('Missing courseId or userId');
    return null;
  }

  const handleSubmit = (
    { name }: { name: string },
    { setFieldError }: { setFieldError: (field: string, message: string) => void }
  ) => {
    const entityIndex = position + 1;
    const removeId = originalCourse.id;

    return cloneCourse(originalCourseId, name, user?.lastSelectedAccount, user?.userId)
      .then(response => {
        const previousId = pathway.entities[entityIndex - 1]?.id;
        const nextId = pathway.entities[entityIndex]?.id;
        const clonedCourseId = response.id;

        return addCoursesToPathway(pathway.id, [clonedCourseId], previousId, nextId, user?.userId)
          .then(data => {
            const newEntityId = data.step.id;
            return removeStepFromPathway(trainingId, removeId, user?.userId)
              .then(() => {
                windowService.redirectTo(
                  `${manageTrainingRoutePrefix}/courses/${clonedCourseId}?workflowId=${trainingId}`
                );
              })
              .catch(err => {
                removeEntity(newEntityId);
                throw Error;
              });
          })
          .catch(err => {
            removeCourse(clonedCourseId, user?.lastSelectedAccount, user?.userId);
            throw Error;
          });
      })
      .catch(err => {
        if (err.eventType === 'TaskListNameDuplicateError') {
          return setFieldError('name', 'This course name already exists. Please select another name.');
        }
        handleError(err);
      });
  };

  return (
    <ModalContainer dismissHandler={handleDismiss}>
      <ModalHeader>
        <h3>Copy Course</h3>
      </ModalHeader>

      <Formik
        initialValues={{ name: originalCourse.name }}
        validationSchema={Yup.object({
          name: Yup.string().required('A name is required'),
        })}
        onSubmit={handleSubmit}
      >
        {({ isSubmitting }) => (
          <Form>
            <ModalBody>
              <HelpAndInfoBox
                title=""
                description="Your church does not own this course. Editing it will create a separate copy in your church's Ministry
                          Grid library."
              />
              <FormField label="Name" name="name" placeholder="Enter Name" />
            </ModalBody>
            <ModalFooter>
              <PrimaryButton type="submit" operating={isSubmitting}>
                Copy and Replace
              </PrimaryButton>
              <MediumButton onClick={handleDismiss}>Cancel</MediumButton>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </ModalContainer>
  );
};
